// Redi Color System — Plaud-minimal palette (Design B port)
// White, near-monochrome screens with warm gray neutrals. Actions are
// ink-black (`action`/`actionOn`): black pill buttons, black + tab, ink
// focus rings. Pine leaves the button system and survives as the semantic
// `primary`/`progress` family — "get to green" still colors readiness,
// meters, and map markers. Risk stays unmistakably brick. Category hexes
// (`progress`/`risk`/`partial`) are untouched: category semantics are not
// part of the re-skin. Soft variants stay low-opacity so dot-labels
// whisper, not shout.

export const lightColors = {
  // Backgrounds — white canvas, warm near-white surfaces
  background: '#ffffff',
  surface: '#f7f7f6',
  surfaceAlt: '#fafaf9',
  surfaceElevated: '#ffffff',
  // Plate behind a third-party logo (insurer marks in BrandTile/BrandStrip).
  // Stays white in both themes: partner logos are drawn for white and are not
  // ours to recolour.
  logoPlate: '#ffffff',
  // Frosted-glass surface for chrome that floats directly over the map
  // (MapPathsOverlay). Derived from `surface`. Web pairs this with a
  // ~14px backdrop blur (see MapPathsOverlay), so the alpha alone can stay
  // light — the blur is what holds text contrast over an unpredictable
  // basemap underneath. Native has no blur, so it uses the more opaque
  // `surfaceTranslucentSolid` sibling instead.
  surfaceTranslucent: 'rgba(247, 247, 246, 0.92)',
  // Native fallback for `surfaceTranslucent` — no backdrop blur is
  // available off web, so this leans on opacity alone to hold contrast.
  surfaceTranslucentSolid: 'rgba(247, 247, 246, 0.96)',

  // Borders — hairlines carry in-page separation (not shadows)
  border: '#ececea', // hairline
  borderLight: '#f2f2f1',
  borderStrong: '#dededb', // secondary-button borders, sheet grabbers
  borderFocus: '#161615', // ink focus ring + input focus

  // Text — warm gray ramp
  text: '#161615', // ink — 17.6:1 on white
  textSecondary: '#6d6d6a', // 5.6:1 on white — AA for body text
  textTertiary: '#9d9d99', // hints/placeholders/disabled only (sub-AA by design)
  textInverse: '#ffffff',

  // Action — ink fills for buttons, the + tab, and other CTAs
  action: '#161615',
  actionOn: '#ffffff',

  // Primary - Evergreen (deep pine), now semantic-only (progress, links)
  primary: '#2e6b50', // 6.3:1 on white — AA as link/text color
  primaryLight: '#4f8f72',
  primaryDark: '#1f4d38',
  primaryStrong: '#1f4d38', // legacy CTA fill — migrate call sites to `action`
  primarySoft: 'rgba(46, 107, 80, 0.08)',

  // Observation colors
  progress: '#2d6a4f', // Deep forest — completed work. 6.4:1 on white
  progressLight: '#4f9d75',
  progressSoft: 'rgba(45, 106, 79, 0.08)',

  // Readiness-only since Aug 2026 (the observation category has its own
  // `opportunity` family below; destructive buttons use the danger family).
  risk: '#a8322d', // Muted brick — work needed. 6.7:1 on white
  riskLight: '#c04a44',
  riskSoft: 'rgba(168, 50, 45, 0.08)',
  riskBorder: 'rgba(168, 50, 45, 0.35)',

  // Opportunity — red again at the client's ask (Aug 25 2026), keeping the
  // "Opportunity" label. On Aug 20 it went blue on the opposite ask ("the red
  // is too alarming"); both asks are real and this is the current one.
  // It does NOT reuse the `risk` brick above. That was the pre-Aug-20 state,
  // and it drew an observation dot and a "work needed" property pin in the
  // same color, separated only by shape — the collision the marker grammar
  // exists to prevent.
  // Warmed from the first attempt at #c00f3c on Aug 26: at hue 345 that read
  // pink rather than red (client). Hue 352 with the saturation up reads as a
  // vivid red and, as it turns out, separates further from the brick (dE 22,
  // was 18) — the two goals pulled the same way rather than against.
  opportunity: '#cc0d26', // 5.75:1 on white
  opportunityLight: '#dc3346', // 4.56:1 on white
  opportunitySoft: 'rgba(204, 13, 38, 0.08)',

  // News — editorial violet, the third observation category alongside
  // progress/opportunity. It moved off indigo-blue when opportunity took the
  // blue slot (Aug 2026). Opportunity went red on Aug 25 and the blue slot is
  // free again, but News stays violet: nobody asked it to move, and the hue
  // is still clear of mapMarkerDelta's cyan.
  news: '#6b3fa0', // 7.7:1 on white
  newsLight: '#8a63bd',
  newsSoft: 'rgba(107, 63, 160, 0.08)',

  partial: '#b45309', // deep amber — in-progress (33–66%). 5.0:1 on white (AA as text)
  partialLight: '#d97706',
  partialSoft: 'rgba(180, 83, 9, 0.08)',

  // Neutral — claimed but not started. Warm stone, deliberately distinct from
  // the slate (#64748b) used for unclaimed map markers.
  neutral: '#78716c',
  neutralLight: '#a8a29e',
  neutralSoft: 'rgba(120, 113, 108, 0.08)',

  // Semantic (aliased to observation colors)
  success: '#2d6a4f',
  successLight: '#4f9d75',
  successSoft: 'rgba(45, 106, 79, 0.08)',
  danger: '#a8322d',
  dangerLight: '#c04a44',
  dangerSoft: 'rgba(168, 50, 45, 0.08)',
  dangerBorder: 'rgba(168, 50, 45, 0.35)', // bordered destructive buttons
  // Fill for white-on-red chips (unread badges). Same deep red in both
  // modes: the content is fixed white, so lightening the fill in dark mode
  // drops it below AA. Dark
  // `danger` (#cf6660) under white 10px text measures 3.66.
  dangerStrong: '#a8322d',
  warning: '#b45309', // de-aliased from brand orange — AA as text
  warningLight: '#d97706',
  warningSoft: 'rgba(180, 83, 9, 0.08)',

  // Utility
  black: '#000000',
  white: '#ffffff',
  overlay: 'rgba(0, 0, 0, 0.5)',
  overlayLight: 'rgba(0, 0, 0, 0.3)',
  divider: 'rgba(0, 0, 0, 0.06)',

  // Interaction state layers (web hover/pressed washes — see useWebHover)
  hoverSurface: 'rgba(22, 22, 21, 0.05)',
  pressedSurface: 'rgba(22, 22, 21, 0.09)',
  fillHover: 'rgba(255, 255, 255, 0.14)', // overlay on solid (ink) CTA fills

  // Components
  avatarPlaceholder: '#ececea',
  skeletonBase: '#efefed',
  skeletonHighlight: '#f8f8f7',

  // Ground behind a presentation photo (map/slideshow). Dark in BOTH
  // themes on purpose: the stage is a cinema, it holds a blurred dark
  // backdrop, and a light ground under a slow-loading photo is the white
  // flash this token exists to kill.
  presentationStage: '#111110',

  // Map markers
  mapMarkerReady: '#2d6a4f',
  mapMarkerProgress: '#2d6a4f',
  mapMarkerOpportunity: '#cc0d26',
  mapMarkerNews: '#6b3fa0',
  mapMarkerDelta: '#1d6fa5', // before/after badge — the only blue on the map
  mapMarkerSelected: '#c2410c', // the one earned brand-orange pop against green/red/slate
  // Community programs (Firewise USA sites, California fire safe councils).
  // A recognized community has no readiness state, so it gets a fixed color
  // rather than borrowing one that means something else. Teal is the only
  // hue left unclaimed by ready/progress (green), opportunity (red), news
  // (violet), delta (blue) and selected (orange).
  mapMarkerCommunity: '#0f766e',

  // Tab bar (light mode) — solid white with a hairline top border (Jason's
  // call: no translucency)
  tabBarBackground: '#ffffff',
  tabBarBorder: '#ececea',
  tabBarActive: '#161615',
  tabBarInactive: '#9d9d99',

  // Additional semantic colors
  backgroundSecondary: '#f7f7f6',
  info: '#64748b',
  infoSoft: 'rgba(100, 116, 139, 0.08)',
  accent: '#c2410c', // demoted brand orange — 5.2:1 on white (AA as text)
  accentSoft: 'rgba(194, 65, 12, 0.08)',

  // Map markers
  mapMarkerClaimed: '#2d6a4f',
  mapMarkerAvailable: '#64748b',

  // Google sign-in button (matches Google's brand guidelines, not the
  // evergreen palette — this button is a recognizable third-party mark)
  googleButtonBackground: '#ffffff',
  googleButtonBorder: '#747775',
  googleButtonText: '#1f1f1f',
};

export const darkColors = {
  // Backgrounds — true near-black, warm cast (Plaud's membership black)
  background: '#0e0e0d',
  surface: '#171716',
  surfaceAlt: '#141413',
  surfaceElevated: '#171716',
  // Plate behind a third-party logo (insurer marks in BrandTile/BrandStrip).
  // Stays white in both themes: partner logos are drawn for white and are not
  // ours to recolour.
  logoPlate: '#ffffff',
  // Dark equivalent of the light palette's surfaceTranslucent, derived from
  // this palette's `surface` (#171716 → rgb(23, 23, 22)). Web pairs this
  // with a ~14px backdrop blur; see the light palette's comment.
  surfaceTranslucent: 'rgba(23, 23, 22, 0.92)',
  // Dark equivalent of surfaceTranslucentSolid, the native (no-blur)
  // fallback.
  surfaceTranslucentSolid: 'rgba(23, 23, 22, 0.96)',

  // Borders — warm near-black hairlines
  border: '#262625',
  borderLight: '#1f1f1e',
  borderStrong: '#33332f',
  borderFocus: '#ececea', // inverted-ink focus ring

  // Text — warm gray ramp (mockup dark board)
  text: '#ececea', // 15.6:1 on background
  textSecondary: '#c9c9c5', // 11.7:1 on background
  textTertiary: '#9d9d99', // hints/placeholders/disabled only
  textInverse: '#161615',

  // Action — inverted ink: light pills on the dark canvas
  action: '#ececea',
  actionOn: '#161615',

  // Primary accent - lifted evergreen for dark-mode legibility
  primary: '#69b28c', // 7.1:1 on background — AA as link/text color
  primaryLight: '#8cc7ab',
  primaryDark: '#2a7050',
  primaryStrong: '#2a7050', // solid CTA fill — white label reads at 5.9:1 (AA)
  primarySoft: 'rgba(105, 178, 140, 0.12)',

  // Observation colors — lifted one step for dark mode legibility
  progress: '#4b9b6e', // 5.3:1 on background
  progressLight: '#6bbd8a',
  progressSoft: 'rgba(75, 155, 110, 0.12)',

  // Readiness-only since Aug 2026 — see the light palette's note.
  risk: '#cf6660', // 4.9:1 on background
  riskLight: '#dd8581',
  riskSoft: 'rgba(207, 102, 96, 0.12)',
  riskBorder: 'rgba(207, 102, 96, 0.45)',

  // Opportunity — same hue as the light palette's red. This is the one that
  // read pinkest before (Aug 26): a dark lift raises lightness, and a cool red
  // lifted far enough is simply pink. Fixed by lifting less and saturating
  // more, which keeps it vivid red against the brick's dusty salmon (dE 29,
  // was 20) while still clearing the `risk` contrast (5.55 vs its 5.26).
  opportunity: '#fa4457', // 5.55:1 on background
  opportunityLight: '#fc7d8c', // 7.77:1 on background
  opportunitySoft: 'rgba(250, 68, 87, 0.12)',

  // News — lifted violet for dark-mode legibility, same hue as the light
  // palette's news color.
  news: '#a98fd6', // 6.1:1 on background
  newsLight: '#c1aee4',
  newsSoft: 'rgba(169, 143, 214, 0.12)',

  partial: '#e08b2b', // lifted amber — 6.7:1 on background
  partialLight: '#f5a94a',
  partialSoft: 'rgba(224, 139, 43, 0.12)',

  // Neutral — claimed but not started. Warm stone, lifted for dark mode.
  neutral: '#a8a29e',
  neutralLight: '#d6d3d1',
  neutralSoft: 'rgba(168, 162, 158, 0.12)',

  // Semantic (aliased)
  success: '#4b9b6e',
  successLight: '#6bbd8a',
  successSoft: 'rgba(75, 155, 110, 0.12)',
  danger: '#cf6660',
  dangerLight: '#dd8581',
  dangerSoft: 'rgba(207, 102, 96, 0.12)',
  dangerBorder: 'rgba(207, 102, 96, 0.45)', // bordered destructive buttons
  dangerStrong: '#a8322d', // see the light palette's note — deep in both modes
  warning: '#e08b2b',
  warningLight: '#f5a94a',
  warningSoft: 'rgba(224, 139, 43, 0.12)',

  // Utility
  black: '#000000',
  white: '#ffffff',
  overlay: 'rgba(0, 0, 0, 0.7)',
  overlayLight: 'rgba(0, 0, 0, 0.5)',
  divider: 'rgba(255, 255, 255, 0.08)',

  // Interaction state layers (web hover/pressed washes — see useWebHover)
  hoverSurface: 'rgba(255, 255, 255, 0.06)',
  pressedSurface: 'rgba(255, 255, 255, 0.10)',
  fillHover: 'rgba(0, 0, 0, 0.08)', // overlay on solid (light-pill) CTA fills

  // Components
  avatarPlaceholder: '#262625',
  skeletonBase: '#1c1c1b',
  skeletonHighlight: '#262625',

  // See the light palette's note — the presentation stage is dark in both.
  presentationStage: '#111110',

  // Map markers
  mapMarkerReady: '#4b9b6e',
  mapMarkerProgress: '#4b9b6e',
  mapMarkerOpportunity: '#fa4457',
  mapMarkerNews: '#a98fd6',
  mapMarkerDelta: '#5eb3e0', // before/after badge — lifted for dark-mode legibility
  mapMarkerSelected: '#fb923c',
  mapMarkerCommunity: '#2dd4bf', // lifted for dark-mode legibility, same as mapMarkerDelta

  // Tab bar (dark mode) — solid, flush, hairline top border
  tabBarBackground: '#0e0e0d',
  tabBarBorder: '#262625',
  tabBarActive: '#ececea',
  tabBarInactive: '#84847f',

  // Additional semantic colors
  backgroundSecondary: '#171716',
  info: '#94a3b8',
  infoSoft: 'rgba(148, 163, 184, 0.12)',
  accent: '#fb923c', // demoted brand orange — 7.9:1 on background
  accentSoft: 'rgba(251, 146, 60, 0.12)',

  // Map markers
  mapMarkerClaimed: '#4b9b6e',
  mapMarkerAvailable: '#94a3b8',

  // Google sign-in button — Google's dark-mode variant per its brand
  // guidelines, not the app's dark-mode observation-color lift.
  googleButtonBackground: '#131314',
  googleButtonBorder: '#8e918f',
  googleButtonText: '#e3e3e3',
};

export type ThemeColors = typeof lightColors;

// READINESS_GRADIENTS lived here until July 2026. Design B took gradients
// out of the grammar, and the token spent its last stretch flattened to
// solid duplicate pairs with no call sites — a retired thing still shaped
// like a rule. Readiness fills read the level color straight from
// getReadinessColor() in src/utils/readiness.ts; see ReadinessMeter.

// Theme-invariant whites for content rendered on colored fills (post
// category caption bars). Identical in both modes, so static
// StyleSheets may import these directly instead of hardcoding rgba whites.
export const ON_COLOR = {
  text: '#ffffff',
  textSecondary: 'rgba(255, 255, 255, 0.92)',
  textMuted: 'rgba(255, 255, 255, 0.82)',
  surface: 'rgba(255, 255, 255, 0.06)',
  surfaceSubtle: 'rgba(255, 255, 255, 0.04)',
  surfaceStrong: 'rgba(255, 255, 255, 0.22)',
  fill: '#ffffff',
};

// Mixed-observation cluster fill. Theme-invariant because the marker itself
// is drawn from a static CSS string in leafletHelpers, not a themed React
// tree, and because a cluster sits on the basemap rather than on a surface.
// Slate on purpose: it is the one marker fill that means "no category," so it
// must not be mistakable for progress, opportunity or news. The legend's
// "Mixed" row reads the same constant so the key cannot drift from the map.
export const MAP_CLUSTER_FILL = '#334155';

// Property cluster fill: several homes merged into one house glyph at low
// zoom. Slate, the same ink as the observation cluster, so the marker makes no
// readiness claim. It was orange until Oct 14 2026, when a demo audience read
// the orange as fire. The observation cluster keeps the circle, so shape
// (house vs. disc) now tells the two clusters apart. The legend's "Several
// properties" row reads the same constant.
export const PROPERTY_CLUSTER_FILL = MAP_CLUSTER_FILL;

// Theme-invariant Google brand colors for the "G" logo mark on
// GoogleSignInButton. Identical in both modes per Google's brand
// guidelines — the button's background/border/text are theme-aware
// (googleButtonBackground/Border/Text above) but the mark itself is not.
export const GOOGLE_LOGO = {
  blue: '#4285F4',
  green: '#34A853',
  yellow: '#FBBC05',
  red: '#EA4335',
} as const;
